/**
 * LifecycleTimelinePanel.jsx — Employer v1.1 Phase 0.6 Slice A.
 *
 * Read-only sidebar panel that renders the lifecycle audit history
 * of a single role. Consumes `/api/employer/v1/audit` (Phase 0.3),
 * filters client-side by role_id (per §13.2 G-H default).
 *
 * Pre-code plan §6.2 + §8.1:
 *   - Renders rows where action ∈
 *       (role.created, role.updated, role.published, role.closed,
 *        role.archived).
 *   - Criteria-PATCH `role.updated` rows are collapsed into a single
 *     summary entry ("Criteria refined N times since publish").
 *   - Loading + empty + error states.
 *   - One fetch on mount + manual refresh button (single hit; no
 *     polling — guards W-12 audit-feed hammering).
 *
 * Slice A scope: READ-ONLY. No mutations, no action affordances.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle, Clock, Loader2, RefreshCw, ScrollText,
} from 'lucide-react';

import { getAudit } from '@/lib/employer/rolesApi';

const LIFECYCLE_ACTIONS = new Set([
  'role.created',
  'role.updated',
  'role.published',
  'role.closed',
  'role.archived',
  // RC 1.62 Phase D — Channel-2 signal lifecycle events. Closes
  // Phase D audit Risk G-1 (silent audit-visibility degradation).
  'signal.sent',
  'signal.accepted',
  'signal.declined',
  'signal.deferred',
]);

const ACTION_LABEL = {
  'role.created':     'Created',
  'role.published':   'Published',
  'role.closed':      'Closed',
  'role.archived':    'Archived',
  'role.updated':     'Updated',
  'signal.sent':      'Signal sent',
  'signal.accepted':  'Signal accepted',
  'signal.declined':  'Signal declined',
  'signal.deferred':  'Signal deferred',
};

const ACTION_TONE = {
  'role.created':     'bg-slate-500/10   text-slate-200   border-slate-500/40',
  'role.published':   'bg-emerald-500/10 text-emerald-200 border-emerald-500/40',
  'role.closed':      'bg-rose-500/10    text-rose-200    border-rose-500/40',
  'role.archived':    'bg-slate-500/10   text-slate-200   border-slate-500/40',
  'role.updated':     'bg-amber-500/10   text-amber-200   border-amber-500/40',
  // Signals tone — calm institutional palette, not "marketplace".
  'signal.sent':      'bg-blue-500/10    text-blue-200    border-blue-500/40',
  'signal.accepted':  'bg-emerald-500/10 text-emerald-200 border-emerald-500/40',
  'signal.declined':  'bg-rose-500/10    text-rose-200    border-rose-500/40',
  'signal.deferred':  'bg-slate-500/10   text-slate-200   border-slate-500/40',
};

function fmtTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year:   'numeric',
    month:  'short',
    day:    '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
  });
}

/**
 * Collapse adjacent criteria-PATCH `role.updated` rows into a single
 * summary entry. Returns an array of either:
 *   - { kind: 'event',    row }
 *   - { kind: 'collapsed', count, first_iso, last_iso }
 */
function collapseTimeline(rows) {
  const out = [];
  let bucket = null;
  for (const row of rows) {
    const isCriteriaPatch =
      row.action === 'role.updated' &&
      ((row.payload || {}).surface === 'criteria_patch');
    if (isCriteriaPatch) {
      if (bucket) {
        bucket.count    += 1;
        bucket.last_iso  = row.created_at;
      } else {
        bucket = {
          kind:      'collapsed',
          count:     1,
          first_iso: row.created_at,
          last_iso:  row.created_at,
        };
      }
      continue;
    }
    if (bucket) {
      out.push(bucket);
      bucket = null;
    }
    out.push({ kind: 'event', row });
  }
  if (bucket) out.push(bucket);
  return out;
}

export default function LifecycleTimelinePanel({ roleId }) {
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [rows,    setRows]    = useState([]);

  const load = useCallback(async () => {
    if (!roleId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getAudit({ roleId });
      const filtered = (Array.isArray(data) ? data : [])
        .filter((r) => LIFECYCLE_ACTIONS.has(r.action))
        .sort((a, b) => {
          // newest first
          const ta = new Date(a.created_at).getTime();
          const tb = new Date(b.created_at).getTime();
          return tb - ta;
        });
      setRows(filtered);
    } catch (e) {
      setError(e?.message || 'Failed to load lifecycle');
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => { load(); }, [load]);

  const items = collapseTimeline(rows);

  return (
    <Card data-testid="lifecycle-timeline-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <ScrollText className="h-4 w-4" />
          Lifecycle Timeline
        </CardTitle>
        <Button
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          data-testid="lifecycle-timeline-refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading lifecycle…
          </div>
        ) : error ? (
          <div
            className="flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200"
            data-testid="lifecycle-timeline-error"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <div>{error}</div>
          </div>
        ) : items.length === 0 ? (
          <div
            className="text-sm text-slate-300"
            data-testid="lifecycle-timeline-empty"
          >
            No lifecycle events yet.
          </div>
        ) : (
          <ol className="space-y-3" data-testid="lifecycle-timeline-list">
            {items.map((item, idx) => {
              if (item.kind === 'collapsed') {
                return (
                  <li
                    key={`collapsed-${idx}`}
                    className="flex items-start gap-3 text-sm"
                    data-testid="lifecycle-timeline-collapsed-criteria"
                  >
                    <Clock className="mt-0.5 h-4 w-4 text-amber-300" />
                    <div className="space-y-0.5">
                      <div className="font-medium text-slate-100">
                        Criteria refined {item.count}× since publish
                      </div>
                      <div className="text-xs text-slate-300">
                        Last: {fmtTs(item.last_iso)}
                      </div>
                    </div>
                  </li>
                );
              }
              const r        = item.row;
              const label    = ACTION_LABEL[r.action] || r.action;
              const tone     = ACTION_TONE[r.action]  || 'bg-slate-500/10 text-slate-200 border-slate-500/40';
              const payload  = r.payload || {};
              const planUn   = payload.plan_unactivated === true;
              const counter  = payload.counter_incremented === true
                                ? 'counter +1'
                                : (payload.counter_decremented === true
                                    ? 'counter -1'
                                    : null);
              const fromTo   = (payload.from && payload.to)
                                ? `${payload.from} → ${payload.to}`
                                : null;
              return (
                <li
                  key={r.id}
                  className="flex items-start gap-3 text-sm"
                  data-testid={`lifecycle-timeline-row-${r.action}`}
                >
                  <Badge className={`${tone} border`}>{label}</Badge>
                  <div className="space-y-0.5">
                    {fromTo ? (
                      <div className="font-medium text-slate-100">{fromTo}</div>
                    ) : (
                      <div className="font-medium text-slate-100">{label}</div>
                    )}
                    <div className="text-xs text-slate-300">
                      {fmtTs(r.created_at)}
                    </div>
                    {(planUn || counter) ? (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {planUn ? (
                          <Badge
                            className="bg-amber-500/10 text-amber-200 border border-amber-500/40"
                            data-testid="lifecycle-chip-plan-unactivated"
                          >
                            plan unactivated
                          </Badge>
                        ) : null}
                        {counter ? (
                          <Badge className="bg-slate-500/10 text-slate-200 border border-slate-500/40">
                            {counter}
                          </Badge>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
